<div data-spartan-page class="p-6 md:p-8 space-y-5"
     style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page header -->
  <app-page-header
    eyebrow="Forwarding"
    title="Admin Panel"
    subtitle="Manage system settings and configurations."
  />

  <!-- Info alert -->
  <app-inline-alert
    tone="info"
    message="Phase 1 Focus: Essential modules for quotation creation. Complex settings and dev tools are minimized to reduce clutter."
  />

  <!-- Admin Modules Grid -->
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

    <!-- Vendor Details -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openVendorDetailsModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-ds-accent-light flex items-center justify-center">
          <ng-icon hlm name="lucideBuilding2" class="size-4 text-ds-accent" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Vendor Details</h3>
            <span hlmBadge variant="default" class="rounded-md h-5 text-[11px] shrink-0">Essential</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Manage all vendors: agents, co-loaders, carriers, brokers, etc.</p>
        </div>
      </div>
    </button>

    <!-- Agents -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openAgentsModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-ds-accent-light flex items-center justify-center">
          <ng-icon hlm name="lucideBuilding" class="size-4 text-ds-accent" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Agents</h3>
            <span hlmBadge variant="default" class="rounded-md h-5 text-[11px] shrink-0">Essential</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Supplier side: origin charges, default rates</p>
        </div>
      </div>
    </button>

    <!-- Clients -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openClientsModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-ds-accent-light flex items-center justify-center">
          <ng-icon hlm name="lucideUsers" class="size-4 text-ds-accent" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Clients</h3>
            <span hlmBadge variant="default" class="rounded-md h-5 text-[11px] shrink-0">Essential</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Client records, markup settings</p>
        </div>
      </div>
    </button>

    <!-- Forwarding Charges -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openOperationalChargesModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-amber-50 flex items-center justify-center">
          <ng-icon hlm name="lucideCoins" class="size-4 text-amber-600" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Forwarding Charges</h3>
            <span hlmBadge variant="default" class="rounded-md h-5 text-[11px] shrink-0">Essential</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Define forwarding charges by mode with conditional logic</p>
        </div>
      </div>
    </button>

    <!-- Port Charges -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openPortChargesModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-red-50 flex items-center justify-center">
          <ng-icon hlm name="lucideAnchor" class="size-4 text-red-600" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Port Charges</h3>
            <span hlmBadge variant="default" class="rounded-md h-5 text-[11px] shrink-0">Essential</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Agent-specific origin charges per port</p>
        </div>
      </div>
    </button>

    <!-- Ports -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openPortsModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-stone-100 flex items-center justify-center">
          <ng-icon hlm name="lucideMapPin" class="size-4 text-stone-600" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Ports</h3>
            <span hlmBadge variant="secondary" class="rounded-md h-5 text-[11px] shrink-0">Optional</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Port dropdown references</p>
        </div>
      </div>
    </button>

    <!-- Carriers & Co-Loaders -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openCarriersModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-green-50 flex items-center justify-center">
          <ng-icon hlm name="lucideShip" class="size-4 text-green-600" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Carriers &amp; Co-Loaders</h3>
            <span hlmBadge variant="default" class="rounded-md h-5 text-[11px] shrink-0">Essential</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Shipping carriers (MSC, Maersk, OOCL)</p>
        </div>
      </div>
    </button>

    <!-- Incoterms -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openIncotermsModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-stone-100 flex items-center justify-center">
          <ng-icon hlm name="lucideFileText" class="size-4 text-stone-600" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Incoterms</h3>
            <span hlmBadge variant="secondary" class="rounded-md h-5 text-[11px] shrink-0">Optional</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Controls logic display (e.g., show trucking if EXW)</p>
        </div>
      </div>
    </button>

    <!-- Container Load Types -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openContainerTypesModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-ds-accent-light flex items-center justify-center">
          <ng-icon hlm name="lucideBox" class="size-4 text-ds-accent" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Container Load Types</h3>
            <span hlmBadge variant="secondary" class="rounded-md h-5 text-[11px] shrink-0">Optional</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">FCL, LCL – controls quote logic</p>
        </div>
      </div>
    </button>

    <!-- Charge Basis -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openChargeUnitsModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-blue-50 flex items-center justify-center">
          <ng-icon hlm name="lucideCalculator" class="size-4 text-blue-600" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Charge Basis</h3>
            <span hlmBadge variant="default" class="rounded-md h-5 text-[11px] shrink-0">Essential</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Define charge units with mode mapping (Air, Sea LCL/FCL)</p>
        </div>
      </div>
    </button>

    <!-- Quote Templates -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openQuoteTemplatesModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-amber-50 flex items-center justify-center">
          <ng-icon hlm name="lucideLayoutTemplate" class="size-4 text-amber-600" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Quote Templates</h3>
            <span hlmBadge variant="default" class="rounded-md h-5 text-[11px] shrink-0">Essential</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">HTML templates for quotation generation</p>
        </div>
      </div>
    </button>

    <!-- Rates Pricing -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openAgentPricingModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-green-50 flex items-center justify-center">
          <ng-icon hlm name="lucideReceipt" class="size-4 text-green-600" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Rates Pricing</h3>
            <span hlmBadge variant="default" class="rounded-md h-5 text-[11px] shrink-0">Essential</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Agent-specific pricing per destination and container type</p>
        </div>
      </div>
    </button>

    <!-- Customs Brokerage Fees -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openCustomsBrokerageFeesModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-red-50 flex items-center justify-center">
          <ng-icon hlm name="lucideClipboardCheck" class="size-4 text-red-600" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Customs Brokerage Fees</h3>
            <span hlmBadge variant="default" class="rounded-md h-5 text-[11px] shrink-0">Essential</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Manage customs clearance and brokerage fees</p>
        </div>
      </div>
    </button>

    <!-- Warehouse Fees -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openWarehouseFeesModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-amber-50 flex items-center justify-center">
          <ng-icon hlm name="lucideWarehouse" class="size-4 text-amber-600" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Warehouse Fees</h3>
            <span hlmBadge variant="default" class="rounded-md h-5 text-[11px] shrink-0">Essential</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Warehouse storage and handling charges</p>
        </div>
      </div>
    </button>

    <!-- Trucking Charges -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openTruckingChargesModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-ds-accent-light flex items-center justify-center">
          <ng-icon hlm name="lucideTruck" class="size-4 text-ds-accent" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Trucking Charges</h3>
            <span hlmBadge variant="default" class="rounded-md h-5 text-[11px] shrink-0">Essential</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Inland transportation and delivery fees</p>
        </div>
      </div>
    </button>

    <!-- Other Charges -->
    <button type="button"
      class="rounded-lg border border-border bg-card shadow-sm p-5 text-left hover:border-ds-accent hover:shadow-md transition-all cursor-pointer group"
      (click)="openOtherChargesModal()">
      <div class="flex items-start gap-3">
        <div class="shrink-0 w-9 h-9 rounded-md bg-stone-100 flex items-center justify-center">
          <ng-icon hlm name="lucideLayers" class="size-4 text-stone-600" />
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <h3 class="text-sm font-semibold">Other Charges</h3>
            <span hlmBadge variant="secondary" class="rounded-md h-5 text-[11px] shrink-0">Optional</span>
          </div>
          <p class="text-xs text-muted-foreground mt-1">Miscellaneous charges and fees</p>
        </div>
      </div>
    </button>

  </div>

</div>

<!-- Modals — unchanged, all legacy modal components preserved -->
<app-operational-charges-modal
  [isOpen]="showOperationalChargesModal"
  (closeModal)="closeOperationalChargesModal()">
</app-operational-charges-modal>

<app-charge-basis-modal></app-charge-basis-modal>
<app-ports-modal></app-ports-modal>
<app-port-charges-management-modal></app-port-charges-management-modal>
<app-carriers-management-modal></app-carriers-management-modal>
<app-container-load-types-modal></app-container-load-types-modal>
<app-agent-management-modal></app-agent-management-modal>
<app-client-management-modal></app-client-management-modal>
<app-incoterms-management-modal></app-incoterms-management-modal>
<app-agent-pricing-modal></app-agent-pricing-modal>
<app-quote-templates-modal></app-quote-templates-modal>
<app-vendor-details-modal></app-vendor-details-modal>
<app-customs-brokerage-modal></app-customs-brokerage-modal>
<app-warehouse-fees-modal></app-warehouse-fees-modal>
<app-trucking-charges-modal></app-trucking-charges-modal>
<app-other-charges-modal></app-other-charges-modal>
